import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { defineMessages, useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { Cover } from '../../music24/components/primitives/Cover';
import { CatalogItem } from '../../../redux/api/fymAPI.types';
import { catalogRowOwnership } from './types';
import { catalogArtworkSrc } from './catalogArtwork';
import { CatalogOwnershipPill } from './CatalogOwnershipPill';
import { CatalogSmartLinkCell } from './CatalogSmartLinkCell';
import { CatalogRowMenu, CatalogRowMenuActions } from './CatalogRowMenu';
import { CatalogSongCell } from './song/CatalogSongCell';
import { CatalogFinishSetup } from './CatalogFinishSetup';
import { catalogSongPath } from './song/page/catalogSongPath';
import { formatCatalogDay, formatCatalogRelativeDay } from './catalogDates';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from './Catalog.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CatalogTableRowProps extends CatalogRowMenuActions {
  accountId: number;
  item: CatalogItem;
  onOpenSmartLink: (smartLinkId: number) => void;
  onCreateLink: (item: CatalogItem) => void;
}

const catalogRowMessages = defineMessages({
  openSong: { id: 'app.sml.catalog.row.openSong', defaultMessage: 'Open the song page of {title}' },
});

/**
 * One release: what it is, whose it is, when it lands, its song files, and where it lives.
 *
 * The whole row opens the song page (design song-page a1, owner decision 6): the title is a real
 * link (Cmd-click opens a new tab) that covers the row. The Song cell, the Smartlink cell and the
 * menu sit above it and keep their own target. A draft song also gets "Finish setup" next to the
 * title (design catalog-add-song h4), which opens its next step of the "Add a song" page.
 */
const CatalogTableRowComponent: React.FC<CatalogTableRowProps> = ({
  accountId,
  item,
  onOpenSmartLink,
  onCreateLink,
  onOpenSong,
  onMakeInStudio,
  onOpenRelease,
  onCopyLink,
  onRemove,
  onMerge,
}) => {
  const intl = useIntl();
  const now = useMemo(() => Date.now(), []);
  const href = `/app${catalogSongPath(item.uuid)}`;
  const openSong = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      // A Cmd-, Ctrl- or Shift-click, or a middle click, opens the link as the browser does.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
        return;
      }
      event.preventDefault();
      onOpenSong(item);
    },
    [item, onOpenSong]
  );
  const openSongFiles = useCallback((row: CatalogItem) => onOpenSong(row, 'files'), [onOpenSong]);

  const subtitle = item.releaseType === null ? item.artistName : `${item.releaseType} · ${item.artistName}`;
  const relative = item.releaseDate === null ? undefined : formatCatalogRelativeDay(intl, item.releaseDate, now);

  return (
    <div className={clsx(styles.tableRow, styles.rowWithLink)} data-testid="catalog-row">
      <span className={styles.workCell}>
        <a
          href={href}
          className={clsx(styles.work, styles.workButton, styles.rowLink)}
          aria-label={intl.formatMessage(catalogRowMessages.openSong, { title: item.title })}
          onClick={openSong}
        >
          <Cover src={catalogArtworkSrc(item, 40)} label={item.title} size={40} />
          <span className={styles.workInfo}>
            <span className={styles.workName}>{item.title}</span>
            <span className={styles.workSub}>{subtitle}</span>
          </span>
        </a>
        <CatalogFinishSetup item={item} />
      </span>
      <span>
        <CatalogOwnershipPill ownership={catalogRowOwnership(item)} />
      </span>
      <span className={styles.date}>
        {item.releaseDate === null ? (
          <span className={styles.mutedCell}>
            {intl.formatMessage({ id: 'app.sml.catalog.date.none', defaultMessage: 'no date yet' })}
          </span>
        ) : (
          <>
            {formatCatalogDay(intl, item.releaseDate)}
            {relative !== undefined && <small className={styles.dateRelative}>{relative}</small>}
          </>
        )}
      </span>
      <span className={styles.rowCell}>
        <CatalogSongCell accountId={accountId} item={item} onOpen={openSongFiles} />
      </span>
      <span className={styles.rowCell}>
        <CatalogSmartLinkCell item={item} onOpenSmartLink={onOpenSmartLink} onCreateLink={onCreateLink} />
      </span>
      <span className={clsx(styles.rowEnd, styles.rowCell)}>
        <CatalogRowMenu
          item={item}
          onOpenSong={onOpenSong}
          onMakeInStudio={onMakeInStudio}
          onOpenRelease={onOpenRelease}
          onCopyLink={onCopyLink}
          onRemove={onRemove}
          onMerge={onMerge}
        />
      </span>
    </div>
  );
};

export const CatalogTableRow = TypedMemo(CatalogTableRowComponent);
